import { useCallback } from 'react'
import { GridColDef, GridRowModel, GridApiCommon } from '@mui/x-data-grid'

export interface ExportOptions {
  /** Export format */
  format: 'csv' | 'excel' | 'pdf'
  /** Include only visible columns */
  visibleColumnsOnly?: boolean
  /** Include filtered rows only */
  filteredRowsOnly?: boolean
  /** Custom filename (without extension) */
  filename?: string
  /** Company/department header for branding */
  header?: {
    company?: string
    department?: string
    title?: string
    subtitle?: string
    reportDate?: string
  }
  /** Footer information */
  footer?: {
    generatedBy?: string
    timestamp?: string
    totalRecords?: number
    customText?: string
  }
  /** Column formatting overrides */
  columnFormatters?: Record<string, (value: any) => string>
  /** Include summary rows */
  includeSummary?: boolean
  /** Summary calculations */
  summaryCalculations?: Record<string, 'sum' | 'count' | 'average' | 'min' | 'max'>
}

export interface UseEnhancedExportReturn {
  exportData: (
    apiRef: React.MutableRefObject<GridApiCommon>,
    columns: GridColDef[],
    rows: GridRowModel[],
    options?: Partial<ExportOptions>
  ) => Promise<void>
  isExporting: boolean
}

export const useEnhancedExport = (): UseEnhancedExportReturn => {
  const isExporting = false // Would implement loading state here

  const formatCurrency = (value: any): string => {
    if (value == null || value === '') return ''
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value
    if (isNaN(numValue)) return String(value)
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue)
  }

  const formatDate = (value: any): string => {
    if (!value) return ''
    try {
      const date = new Date(value)
      if (isNaN(date.getTime())) return String(value)
      return new Intl.DateTimeFormat('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date)
    } catch {
      return String(value)
    }
  }

  const formatNumber = (value: any): string => {
    if (value == null || value === '') return ''
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) return String(value)
    return new Intl.NumberFormat('es-CO').format(numValue)
  }

  const getDefaultFormatters = (): Record<string, (value: any) => string> => ({
    currency: formatCurrency,
    date: formatDate,
    number: formatNumber,
    text: (value: any) => String(value || ''),
  })

  const applyColumnFormatter = (
    value: any,
    column: GridColDef,
    customFormatters?: Record<string, (value: any) => string>
  ): string => {
    // Check custom formatters first
    if (customFormatters && customFormatters[column.field]) {
      return customFormatters[column.field](value)
    }

    // Apply default formatting based on column type or field name
    if (column.type === 'number' || column.field.toLowerCase().includes('valor') || 
        column.field.toLowerCase().includes('monto') || column.field.toLowerCase().includes('saldo')) {
      return formatCurrency(value)
    }
    
    if (column.type === 'date' || column.field.toLowerCase().includes('fecha')) {
      return formatDate(value)
    }

    return String(value || '')
  }

  const calculateSummary = (
    rows: GridRowModel[],
    column: GridColDef,
    calculation: 'sum' | 'count' | 'average' | 'min' | 'max'
  ): number => {
    const values = rows
      .map(row => row[column.field])
      .filter(value => value != null && value !== '')
      .map(value => {
        const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value
        return isNaN(numValue) ? 0 : numValue
      })

    switch (calculation) {
      case 'sum':
        return values.reduce((sum, val) => sum + val, 0)
      case 'count':
        return values.length
      case 'average':
        return values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0
      case 'min':
        return values.length > 0 ? Math.min(...values) : 0
      case 'max':
        return values.length > 0 ? Math.max(...values) : 0
      default:
        return 0
    }
  }

  const exportToCSV = (
    data: string[][],
    filename: string,
    _options: ExportOptions
  ): void => {
    const csvContent = data
      .map(row => 
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const escaped = String(cell).replace(/"/g, '""')
          return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
        }).join(',')
      )
      .join('\n')

    // Add BOM for proper UTF-8 encoding in Excel
    const bom = '\uFEFF'
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' })
    
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${filename}.csv`
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  const exportToExcel = async (
    data: string[][],
    filename: string,
    _options: ExportOptions
  ): Promise<void> => {
    // For now, export as CSV with Excel-optimized formatting
    // In a real implementation, you'd use a library like SheetJS
    exportToCSV(data, filename, _options)
  }

  const exportData = useCallback(async (
    _apiRef: React.MutableRefObject<GridApiCommon>,
    columns: GridColDef[],
    rows: GridRowModel[],
    options: Partial<ExportOptions> = {}
  ): Promise<void> => {
    const defaultOptions: ExportOptions = {
      format: 'csv',
      visibleColumnsOnly: true,
      filteredRowsOnly: true,
      filename: `export_${new Date().toISOString().split('T')[0]}`,
      header: {
        company: 'Gerencia de Proyectos - Sistema Automatizado',
        department: 'Automatización de Cuentas por Pagar',
        reportDate: new Date().toLocaleDateString('es-CO'),
      },
      footer: {
        generatedBy: 'Sistema AutoCE',
        timestamp: new Date().toLocaleString('es-CO'),
      },
      columnFormatters: getDefaultFormatters(),
      includeSummary: false,
      summaryCalculations: {},
    }

    const mergedOptions = { ...defaultOptions, ...options }

    try {
      // Get filtered rows if specified
      let exportRows = rows
      // Note: getVisibleRowModels may not be available in this MUI version
      // if (mergedOptions.filteredRowsOnly && _apiRef.current) {
      //   const filteredRows = _apiRef.current.getVisibleRowModels()
      //   exportRows = Array.from(filteredRows.values())
      // }

      // Get visible columns if specified
      let exportColumns = columns
      // Note: getVisibleColumns may not be available in this MUI version
      // if (mergedOptions.visibleColumnsOnly && _apiRef.current) {
      //   const visibleColumns = _apiRef.current.getVisibleColumns()
      //   exportColumns = visibleColumns.filter((col: any) => columns.find(c => c.field === col.field))
      // }

      // Prepare data array
      const data: string[][] = []

      // Add header
      if (mergedOptions.header) {
        if (mergedOptions.header.company) {
          data.push([mergedOptions.header.company])
        }
        if (mergedOptions.header.department) {
          data.push([mergedOptions.header.department])
        }
        if (mergedOptions.header.title) {
          data.push([mergedOptions.header.title])
        }
        if (mergedOptions.header.subtitle) {
          data.push([mergedOptions.header.subtitle])
        }
        if (mergedOptions.header.reportDate) {
          data.push([`Fecha del reporte: ${mergedOptions.header.reportDate}`])
        }
        data.push([]) // Empty row separator
      }

      // Add column headers
      const headers = exportColumns.map(col => col.headerName || col.field)
      data.push(headers)

      // Add data rows
      exportRows.forEach(row => {
        const rowData = exportColumns.map(col => 
          applyColumnFormatter(row[col.field], col, mergedOptions.columnFormatters)
        )
        data.push(rowData)
      })

      // Add summary if specified
      if (mergedOptions.includeSummary && mergedOptions.summaryCalculations) {
        data.push([]) // Empty row separator
        data.push(['RESUMEN'])
        
        Object.entries(mergedOptions.summaryCalculations).forEach(([field, calculation]) => {
          const column = exportColumns.find(col => col.field === field)
          if (column) {
            const summaryValue = calculateSummary(exportRows, column, calculation)
            const formattedValue = applyColumnFormatter(summaryValue, column, mergedOptions.columnFormatters)
            data.push([`${calculation.toUpperCase()} ${column.headerName || field}:`, formattedValue])
          }
        })
      }

      // Add footer
      if (mergedOptions.footer) {
        data.push([]) // Empty row separator
        if (mergedOptions.footer.totalRecords) {
          data.push([`Total de registros: ${exportRows.length}`])
        }
        if (mergedOptions.footer.generatedBy) {
          data.push([`Generado por: ${mergedOptions.footer.generatedBy}`])
        }
        if (mergedOptions.footer.timestamp) {
          data.push([`Fecha de generación: ${mergedOptions.footer.timestamp}`])
        }
        if (mergedOptions.footer.customText) {
          data.push([mergedOptions.footer.customText])
        }
      }

      // Export based on format
      switch (mergedOptions.format) {
        case 'csv':
          exportToCSV(data, mergedOptions.filename!, mergedOptions)
          break
        case 'excel':
          await exportToExcel(data, mergedOptions.filename!, mergedOptions)
          break
        case 'pdf':
          // PDF export would be implemented here
          console.warn('PDF export not yet implemented')
          break
        default:
          exportToCSV(data, mergedOptions.filename!, mergedOptions)
      }

    } catch (error) {
      console.error('Export failed:', error)
      throw new Error('Error al exportar los datos. Por favor, inténtelo de nuevo.')
    }
  }, [])

  return {
    exportData,
    isExporting,
  }
}

export default useEnhancedExport